import { useState, useEffect } from 'react';
import { TextField, Stack, Button, Divider, Select, MenuItem, Typography, CircularProgress, Box, LinearProgress } from '@mui/material';
import OnboardingLayout from 'layout/OnboardingLayout';
import AuthCardWrapper from '../AuthCardWrapper';
import LogoBg from '../../../../assets/images/logo_bg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from 'api/authService';
import marketCategoryService from 'api/marketCategoryService';

// Password strength utility
export const getPasswordStrength = (password) => {
    if (!password) return { label: 'Weak', score: 0, color: '#d32f2f' };
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    score = Math.min(score, 5);

    if (score <= 1) return { label: 'Weak', score, color: '#d32f2f' };
    if (score <= 3) return { label: 'Moderate', score, color: '#ffa000' };
    return { label: 'Strong', score, color: '#2e7d32' };
};

const VendorInformation = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const tempToken = state?.tempToken;
    const email = state?.email;

    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [categoriesError, setCategoriesError] = useState('');

    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        gender: 'male',
        password: '',
        confirmPassword: '',
        vendorName: '',
        vendorPhone: '',
        vendorDescription: '',
        marketCategoryId: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({ label: 'Weak', score: 0, color: '#d32f2f' });

    // Redirect if no tempToken (user refresh)
    useEffect(() => {
        if (!tempToken || !email) {
            navigate('/register', { replace: true });
        }
    }, []);

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoadingCategories(true);
                const res = await marketCategoryService.all();
                console.log(res);
                setCategories(res.data);
                setForm((prev) => ({ ...prev, marketCategoryId: res[0]?.id || '' }));
            } catch (err) {
                setCategoriesError('Failed to load categories');
            } finally {
                setLoadingCategories(false);
            }
        };

        fetchCategories();
    }, []);

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: null }));

        if (key === 'password') {
            const strength = getPasswordStrength(value);
            setPasswordStrength(strength);

            if (form.confirmPassword && form.confirmPassword !== value) {
                setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
            } else {
                setErrors((prev) => ({ ...prev, confirmPassword: null }));
            }
        }

        if (key === 'confirmPassword') {
            if (value !== form.password) {
                setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }));
            } else {
                setErrors((prev) => ({ ...prev, confirmPassword: null }));
            }
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!form.firstName) newErrors.firstName = 'First name is required';
        if (!form.lastName) newErrors.lastName = 'Last name is required';
        if (!form.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
        if (!form.password) newErrors.password = 'Password is required';
        if (form.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        if (!form.vendorName) newErrors.vendorName = 'Vendor name is required';
        if (!form.vendorPhone) newErrors.vendorPhone = 'Vendor phone is required';
        if (!form.vendorEmail) newErrors.vendorEmail = 'Vendor email is required';
        if (!form.marketCategoryId) newErrors.marketCategoryId = 'Market category is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);

        try {
            await authService.completeProfile(
                {
                    firstName: form.firstName,
                    lastName: form.lastName,
                    phoneNumber: form.phoneNumber,
                    gender: form.gender,
                    password: form.password,
                    vendor: {
                        name: form.vendorName,
                        phoneNumber: form.vendorPhone,
                        email: form.vendorEmail,
                        marketCategoryId: form.marketCategoryId,
                        description: form.vendorDescription
                    }
                },
                tempToken
            );

            navigate('/login', { replace: true });
        } finally {
            setLoading(false);
        }
    };

    return (
        <OnboardingLayout
            step={3}
            totalSteps={3}
            title="Vendor Information"
            subtitle="Complete your profile"
            backLink="/register"
            imageSrc={LogoBg}
        >
            <Stack spacing={3}>
                <AuthCardWrapper>
                    <Stack spacing={2}>
                        <>
                            {/* User Info */}
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                    label="First Name"
                                    fullWidth
                                    value={form.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                />
                                <TextField
                                    label="Last Name"
                                    fullWidth
                                    value={form.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <TextField
                                    label="Phone Number"
                                    fullWidth
                                    value={form.phoneNumber}
                                    onChange={(e) => handleChange('phoneNumber', e.target.value)}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber}
                                />
                                <Select fullWidth value={form.gender} onChange={(e) => handleChange('gender', e.target.value)}>
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                </Select>
                            </Stack>

                            {/* Password */}
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Box flex={1}>
                                    <TextField
                                        type="password"
                                        label="Password"
                                        fullWidth
                                        value={form.password}
                                        onChange={(e) => handleChange('password', e.target.value)}
                                        error={!!errors.password}
                                        helperText={errors.password}
                                    />
                                    <Box mt={1}>
                                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: passwordStrength.color }}>
                                            {passwordStrength.label}
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(passwordStrength.score / 5) * 100}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                '& .MuiLinearProgress-bar': { backgroundColor: passwordStrength.color }
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <TextField
                                    type="password"
                                    label="Confirm Password"
                                    fullWidth
                                    value={form.confirmPassword}
                                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                    sx={{ flex: 1 }}
                                />
                            </Stack>

                            <Divider />

                            {/* Vendor Details */}
                            <Stack spacing={2}>
                                <TextField
                                    label="Vendor Name"
                                    fullWidth
                                    value={form.vendorName}
                                    onChange={(e) => handleChange('vendorName', e.target.value)}
                                    error={!!errors.vendorName}
                                    helperText={errors.vendorName}
                                />
                                <TextField
                                    label="Vendor Phone"
                                    fullWidth
                                    value={form.vendorPhone}
                                    onChange={(e) => handleChange('vendorPhone', e.target.value)}
                                    error={!!errors.vendorPhone}
                                    helperText={errors.vendorPhone}
                                />
                                <TextField
                                    label="Vendor Email"
                                    fullWidth
                                    value={form.vendorEmail}
                                    onChange={(e) => handleChange('vendorEmail', e.target.value)}
                                    error={!!errors.vendorEmail}
                                    helperText={errors.vendorEmail}
                                />
                                <TextField
                                    label="Description"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={form.vendorDescription}
                                    onChange={(e) => handleChange('vendorDescription', e.target.value)}
                                />

                                <Typography variant="subtitle2">Market Category</Typography>
                                <Select
                                    fullWidth
                                    value={form.marketCategoryId}
                                    onChange={(e) => handleChange('marketCategoryId', e.target.value)}
                                >
                                    <MenuItem value="" disabled>
                                        {loadingCategories ? 'Loading...' : categoriesError || 'Select Category'}
                                    </MenuItem>
                                    {/* no category error, no category loading */}
                                    {!loadingCategories &&
                                        !categoriesError &&
                                        categories.map((cat) => (
                                            <MenuItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </Stack>

                            <Button variant="contained" size="large" fullWidth onClick={handleSubmit} disabled={loading} sx={{ mt: 2 }}>
                                {loading ? <CircularProgress size={20} thickness={5} sx={{ color: 'white' }} /> : 'Complete Registration'}
                            </Button>
                        </>
                    </Stack>
                </AuthCardWrapper>
            </Stack>
        </OnboardingLayout>
    );
};

export default VendorInformation;
